import React, { useContext, useEffect, useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import SelectBox from '../../../../atomic/selectbox/SelectBox';
import { GlobalStateDataContext } from '../../../../context-api/data-provider/GlobalStateDataProvider';
import { normalizeTimeTo12Hour } from '../storeSettingsForm';
import {
  SS_OUTLINED_INPUT,
  ssFieldSx,
  grid2,
} from '../storeSettingsConstants';
import { ToggleCard } from '../components/ToggleCard';
import {
  PROMO_TYPE_OPTIONS,
  PROMO_CONDITION_OPTIONS,
  PROMO_APPLICABLE_TO_OPTIONS,
  PROMO_MENU_OPTIONS,
  getEmptyCouponForm,
  buildCouponPayloadFromForm,
  isPercentagePromoType,
  isHappyHourPromoType,
  isFreeProductPromoType,
  isAmountPromoType,
} from '../couponForm';

dayjs.extend(customParseFormat);

const parseTime = (value) => {
  const normalized = normalizeTimeTo12Hour(value);
  if (!normalized) return null;
  const parsed = dayjs(normalized, 'hh:mm A');
  return parsed.isValid() ? parsed : null;
};

const formatTime = (value) => {
  if (!value || !value.isValid?.()) return '';
  return value.format('hh:mm A');
};

const parseDate = (value) => {
  const raw = String(value ?? '').trim();
  if (!raw) return null;
  const parsed = dayjs(raw, 'YYYY-MM-DD', true);
  return parsed.isValid() ? parsed : null;
};

const formatDate = (value) => {
  if (!value || !value.isValid?.()) return '';
  return value.format('YYYY-MM-DD');
};

const datePickerSx = {
  width: '100%',
  '& .MuiInputBase-input': { fontSize: '14px' },
};

export function CreateCouponModal({ open, onClose, onCreate }) {
  const { onLoadInfoResponse } = useContext(GlobalStateDataContext);
  const kitchenInfo = onLoadInfoResponse?.data?.data?.data?.KitchenInfo;
  const managingBranch =
    onLoadInfoResponse?.data?.data?.data?.managing_branch_name ||
    kitchenInfo?.managing_branch_name ||
    null;

  const [form, setForm] = useState(getEmptyCouponForm);
  const [errors, setErrors] = useState({});
  const [openStartHh, setOpenStartHh] = useState(false);
  const [openEndHh, setOpenEndHh] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(getEmptyCouponForm());
      setErrors({});
    }
  }, [open]);

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const showPercentageCap = isPercentagePromoType(form.type);
  const showHappyHour = isHappyHourPromoType(form.type);
  const showProduct = isFreeProductPromoType(form.type);
  const showAmountAdornment = isAmountPromoType(form.type) || form.type === 'free_delivery';
  const showPercentAdornment = isPercentagePromoType(form.type);
  const showDedicatedEmails = form.applicableTo === 'dedicatedCustomer';

  const valueLabel = useMemo(() => {
    if (showPercentAdornment) return 'Discount (%)';
    if (showAmountAdornment) return 'Discount amount';
    if (form.type === 'free_order') return 'Order value covered';
    return 'Value';
  }, [form.type, showAmountAdornment, showPercentAdornment]);

  const validate = () => {
    const next = {};
    const code = String(form.code ?? '')
      .trim()
      .toLowerCase();
    if (!code) next.code = 'Code is required';
    else if (code.length > 15) next.code = 'Max 15 characters';
    else if (!/^[a-z0-9_-]+$/.test(code)) next.code = 'Use letters, numbers, - or _ only';

    if (!form.type) next.type = 'Type is required';
    if (form.value === '' || form.value == null || Number.isNaN(Number(form.value))) {
      next.value = 'Value is required';
    }
    if (!form.startDate) next.startDate = 'Start date is required';
    if (!form.conditionType) next.conditionType = 'Audience is required';
    if (!form.applicableTo) next.applicableTo = 'Required';

    if (showDedicatedEmails) {
      const emails = String(form.dedicatedCustomerEmail ?? '')
        .split(/[,;\n]+/)
        .map((e) => e.trim())
        .filter(Boolean);
      if (emails.length === 0) next.dedicatedCustomerEmail = 'Add at least one email';
    }

    if (showProduct && !String(form.product_name ?? '').trim()) {
      next.product_name = 'Product name is required';
    }

    if (showHappyHour) {
      if (!normalizeTimeTo12Hour(form.happy_hour_start_time)) {
        next.happy_hour_start_time = 'Start time required';
      }
      if (!normalizeTimeTo12Hour(form.happy_hour_end_time)) {
        next.happy_hour_end_time = 'End time required';
      }
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    const base = buildCouponPayloadFromForm(form);
    const payload = {
      ...base,
      createdBy: String(kitchenInfo?.kitchenName || kitchenInfo?.email || 'kitchen')
        .trim()
        .toLowerCase(),
      managing_branch_name: managingBranch
        ? String(managingBranch).trim().toLowerCase()
        : null,
      account_type: kitchenInfo?.account_type
        ? String(kitchenInfo.account_type).trim().toLowerCase()
        : 'none',
      promotedBy: 'none',
    };

    onCreate?.(payload);
    onClose?.();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth='sm'
      className='ss-create-coupon-modal'
      aria-labelledby='create-coupon-modal-title'
    >
      <div className='ss-create-coupon-modal__header'>
        <div>
          <h2 id='create-coupon-modal-title' className='ss-create-coupon-modal__title'>
            Create Coupon
          </h2>
          <p className='ss-create-coupon-modal__subtitle'>
            Set the code, discount, audience, and schedule for this offer.
          </p>
        </div>
        <IconButton aria-label='Close' onClick={onClose} size='small' sx={{ color: '#6b7280' }}>
          <CloseIcon fontSize='small' />
        </IconButton>
      </div>

      <div className='ss-create-coupon-modal__body'>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <div className='ss-create-coupon-modal__section-title'>Basics</div>
          <div style={{ ...grid2, marginBottom: 14 }}>
            <TextField
              className={SS_OUTLINED_INPUT}
              size='small'
              label='Promo code'
              value={form.code}
              onChange={(e) =>
                updateField(
                  'code',
                  e.target.value
                    .toLowerCase()
                    .replace(/[^a-z0-9_-]/g, '')
                    .slice(0, 15)
                )
              }
              error={Boolean(errors.code)}
              helperText={errors.code || 'Max 15 characters'}
              variant='outlined'
              sx={{
                ...ssFieldSx,
                '& .MuiInputBase-input': { fontSize: '14px', textTransform: 'none' },
              }}
              inputProps={{ maxLength: 15 }}
            />
            <SelectBox
              sx={{ width: '100%' }}
              label='Coupon type'
              value={form.type}
              onChange={(value) => updateField('type', value)}
              options={PROMO_TYPE_OPTIONS.map((o) => o.value)}
              formatOptionLabel={(v) => PROMO_TYPE_OPTIONS.find((o) => o.value === v)?.label ?? v}
              error={Boolean(errors.type)}
              helperText={errors.type}
            />
          </div>

          <TextField
            className={SS_OUTLINED_INPUT}
            size='small'
            label='Promo label'
            value={form.promo_label}
            onChange={(e) => updateField('promo_label', e.target.value.slice(0, 200))}
            helperText='Shown on the coupon card (optional)'
            variant='outlined'
            sx={{ ...ssFieldSx, mb: 1.5 }}
            fullWidth
          />

          <div style={{ ...grid2, marginBottom: 14 }}>
            <TextField
              className={SS_OUTLINED_INPUT}
              size='small'
              label={valueLabel}
              value={form.value}
              onChange={(e) => {
                const raw = e.target.value.replace(/[^0-9.]/g, '');
                updateField('value', raw);
              }}
              error={Boolean(errors.value)}
              helperText={errors.value}
              InputProps={{
                startAdornment: showAmountAdornment ? (
                  <InputAdornment position='start'>$</InputAdornment>
                ) : undefined,
                endAdornment: showPercentAdornment ? (
                  <InputAdornment position='end'>%</InputAdornment>
                ) : undefined,
              }}
              variant='outlined'
              sx={ssFieldSx}
            />
            <TextField
              className={SS_OUTLINED_INPUT}
              size='small'
              label='Minimum order value'
              value={form.minimumOrderValue}
              onChange={(e) => {
                const raw = e.target.value.replace(/[^0-9.]/g, '');
                updateField('minimumOrderValue', raw);
              }}
              InputProps={{
                startAdornment: <InputAdornment position='start'>$</InputAdornment>,
              }}
              variant='outlined'
              sx={ssFieldSx}
            />
          </div>

          {showPercentageCap && (
            <TextField
              className={SS_OUTLINED_INPUT}
              size='small'
              label='Percentage off cap'
              value={form.percentage_off_cap}
              onChange={(e) => updateField('percentage_off_cap', e.target.value.slice(0, 10))}
              helperText='e.g. 8 for “20% off, up to $8”'
              InputProps={{
                startAdornment: <InputAdornment position='start'>$</InputAdornment>,
              }}
              variant='outlined'
              sx={{ ...ssFieldSx, mb: 1.5 }}
              fullWidth
            />
          )}

          {showProduct && (
            <TextField
              className={SS_OUTLINED_INPUT}
              size='small'
              label='Product name'
              value={form.product_name}
              onChange={(e) => updateField('product_name', e.target.value.slice(0, 100))}
              error={Boolean(errors.product_name)}
              helperText={errors.product_name || 'Required for free product / combo'}
              variant='outlined'
              sx={{ ...ssFieldSx, mb: 1.5 }}
              fullWidth
            />
          )}

          {showHappyHour && (
            <div style={{ ...grid2, marginBottom: 14 }}>
              <TimePicker
                open={openStartHh}
                onOpen={() => setOpenStartHh(true)}
                onClose={() => setOpenStartHh(false)}
                value={parseTime(form.happy_hour_start_time)}
                onChange={(value) => updateField('happy_hour_start_time', formatTime(value))}
                label='Happy hour start'
                ampm
                slotProps={{
                  textField: {
                    size: 'small',
                    className: SS_OUTLINED_INPUT,
                    error: Boolean(errors.happy_hour_start_time),
                    helperText: errors.happy_hour_start_time,
                    sx: ssFieldSx,
                  },
                }}
              />
              <TimePicker
                open={openEndHh}
                onOpen={() => setOpenEndHh(true)}
                onClose={() => setOpenEndHh(false)}
                value={parseTime(form.happy_hour_end_time)}
                onChange={(value) => updateField('happy_hour_end_time', formatTime(value))}
                label='Happy hour end'
                ampm
                slotProps={{
                  textField: {
                    size: 'small',
                    className: SS_OUTLINED_INPUT,
                    error: Boolean(errors.happy_hour_end_time),
                    helperText: errors.happy_hour_end_time,
                    sx: ssFieldSx,
                  },
                }}
              />
            </div>
          )}

          <div className='ss-create-coupon-modal__section-title'>Audience</div>
          <div style={{ ...grid2, marginBottom: 14 }}>
            <SelectBox
              sx={{ width: '100%' }}
              label='Condition'
              value={form.conditionType}
              onChange={(value) => updateField('conditionType', value)}
              options={PROMO_CONDITION_OPTIONS.map((o) => o.value)}
              formatOptionLabel={(v) =>
                PROMO_CONDITION_OPTIONS.find((o) => o.value === v)?.label ?? v
              }
              error={Boolean(errors.conditionType)}
              helperText={errors.conditionType}
            />
            <SelectBox
              sx={{ width: '100%' }}
              label='Applicable to'
              value={form.applicableTo}
              onChange={(value) => updateField('applicableTo', value)}
              options={PROMO_APPLICABLE_TO_OPTIONS.map((o) => o.value)}
              formatOptionLabel={(v) =>
                PROMO_APPLICABLE_TO_OPTIONS.find((o) => o.value === v)?.label ?? v
              }
              error={Boolean(errors.applicableTo)}
              helperText={errors.applicableTo}
            />
          </div>

          <SelectBox
            sx={{ width: '100%', marginBottom: 14 }}
            label='Applicable menu'
            value={form.applicable_menu}
            onChange={(value) => updateField('applicable_menu', value)}
            options={PROMO_MENU_OPTIONS.map((o) => o.value)}
            formatOptionLabel={(v) => PROMO_MENU_OPTIONS.find((o) => o.value === v)?.label ?? v}
          />

          {showDedicatedEmails && (
            <TextField
              className={SS_OUTLINED_INPUT}
              size='small'
              label='Dedicated customer emails'
              value={form.dedicatedCustomerEmail}
              onChange={(e) => updateField('dedicatedCustomerEmail', e.target.value)}
              error={Boolean(errors.dedicatedCustomerEmail)}
              helperText={errors.dedicatedCustomerEmail || 'Separate multiple emails with commas'}
              variant='outlined'
              sx={{ ...ssFieldSx, mb: 1.5 }}
              fullWidth
              multiline
              minRows={2}
            />
          )}

          <div className='ss-create-coupon-modal__section-title'>Schedule</div>
          <div style={{ ...grid2, marginBottom: 14 }}>
            <DatePicker
              label='Start date'
              value={parseDate(form.startDate)}
              onChange={(value) => updateField('startDate', formatDate(value))}
              slotProps={{
                textField: {
                  size: 'small',
                  className: SS_OUTLINED_INPUT,
                  error: Boolean(errors.startDate),
                  helperText: errors.startDate,
                  sx: datePickerSx,
                },
              }}
            />
            <DatePicker
              label='End date'
              value={parseDate(form.endDate)}
              onChange={(value) => updateField('endDate', formatDate(value))}
              slotProps={{
                textField: {
                  size: 'small',
                  className: SS_OUTLINED_INPUT,
                  sx: datePickerSx,
                },
              }}
            />
          </div>

          <TextField
            className={SS_OUTLINED_INPUT}
            size='small'
            label='Ad image URL'
            value={form.product_image_advertisement_url}
            onChange={(e) => updateField('product_image_advertisement_url', e.target.value)}
            helperText='Optional product advertisement image'
            variant='outlined'
            sx={{ ...ssFieldSx, mb: 1.5 }}
            fullWidth
          />

          <ToggleCard
            label='Promo active'
            description='Turn off to pause this coupon without deleting it.'
            value={form.promo_status}
            onChange={(v) => updateField('promo_status', v)}
          />
        </LocalizationProvider>
      </div>

      <div className='ss-create-coupon-modal__footer'>
        <button type='button' className='ss-create-coupon-modal__btn-secondary' onClick={onClose}>
          Cancel
        </button>
        <button type='button' className='ss-create-coupon-modal__btn-primary' onClick={handleSubmit}>
          Create Coupon
        </button>
      </div>
    </Dialog>
  );
}
